// Case Management - Reports Dashboard
import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Users,
  Briefcase,
  FileText,
  Phone,
  Calendar,
  Download,
  Filter,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRight,
  Activity
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Simple bar chart component
const SimpleBarChart = ({ data, valueKey, labelKey, maxValue, color = 'bg-primary' }) => {
  const max = maxValue || Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div className="space-y-2">
      {data.map((item, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-24 text-sm text-muted-foreground truncate" title={item[labelKey]}>
            {item[labelKey]}
          </div>
          <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full ${color} rounded-full transition-all duration-500`}
              style={{ width: `${((item[valueKey] || 0) / max) * 100}%` }}
            />
          </div>
          <div className="w-16 text-sm font-medium text-right">
            {typeof item[valueKey] === 'number' ? item[valueKey].toLocaleString() : item[valueKey]}
          </div>
        </div>
      ))}
    </div>
  );
};

// Stat card component
const StatCard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'text-primary' }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg bg-muted ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {trend && (
        <div className="flex items-center gap-1 mt-2 text-xs">
          {trend === 'up' ? (
            <TrendingUp className="w-3 h-3 text-emerald-500" />
          ) : (
            <TrendingDown className="w-3 h-3 text-red-500" />
          )}
          <span className={trend === 'up' ? 'text-emerald-500' : 'text-red-500'}>
            {trendValue}
          </span>
        </div>
      )}
    </CardContent>
  </Card>
);

// Pipeline funnel component
const PipelineFunnel = ({ data }) => {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const stageColors = {
    intake: 'bg-blue-500',
    review: 'bg-indigo-500',
    discovery: 'bg-purple-500',
    negotiation: 'bg-pink-500',
    litigation: 'bg-red-500',
    trial: 'bg-orange-500',
    settlement: 'bg-amber-500',
    closing: 'bg-emerald-500',
    appeal: 'bg-teal-500',
    archived: 'bg-slate-400'
  };
  
  return (
    <div className="space-y-2">
      {data.filter(d => d.count > 0).map((stage, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="w-24 text-sm capitalize">{stage.stage}</div>
          <div className="flex-1 relative">
            <div
              className={`h-8 ${stageColors[stage.stage] || 'bg-primary'} rounded transition-all duration-500 flex items-center justify-end pr-3`}
              style={{ width: `${(stage.count / maxCount) * 100}%`, minWidth: '60px' }}
            >
              <span className="text-white text-sm font-medium">{stage.count}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function CaseReportsDashboard() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Report data
  const [caseReport, setCaseReport] = useState(null);
  const [billingReport, setBillingReport] = useState(null);
  const [productivityReport, setProductivityReport] = useState(null);
  const [communicationsReport, setCommunicationsReport] = useState(null);
  const [pipelineReport, setPipelineReport] = useState(null);

  useEffect(() => {
    fetchAllReports();
  }, [token]);

  const fetchAllReports = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const [caseRes, billingRes, productivityRes, commRes, pipelineRes] = await Promise.all([
        fetch(`${API_URL}/api/case-management/reports/case-status${queryString}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/case-management/reports/billing${queryString}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/case-management/reports/productivity${queryString}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/case-management/reports/communications${queryString}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/case-management/reports/pipeline${queryString}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (caseRes.ok) setCaseReport(await caseRes.json());
      if (billingRes.ok) setBillingReport(await billingRes.json());
      if (productivityRes.ok) setProductivityReport(await productivityRes.json());
      if (commRes.ok) setCommunicationsReport(await commRes.json());
      if (pipelineRes.ok) setPipelineReport(await pipelineRes.json());
      
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchAllReports();
  };

  const handleFilterApply = () => {
    handleRefresh();
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);
  };

  const formatHours = (hours) => {
    return `${(hours || 0).toFixed(1)}h`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with filters */}
      <div className="flex flex-wrap gap-4 items-end justify-between">
        <div>
          <h2 className="text-2xl font-bold">Reports & Analytics</h2>
          <p className="text-muted-foreground">Comprehensive insights into your practice</p>
        </div>
        
        <div className="flex gap-3 items-end">
          <div>
            <Label className="text-xs">From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-36"
              data-testid="report-date-from"
            />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-36"
              data-testid="report-date-to"
            />
          </div>
          <Button onClick={handleFilterApply} variant="outline" data-testid="apply-filters-btn">
            <Filter className="w-4 h-4 mr-2" />
            Apply
          </Button>
          <Button onClick={handleRefresh} variant="outline" disabled={refreshing} data-testid="refresh-reports-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <BarChart3 className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">
            <DollarSign className="w-4 h-4 mr-2" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="productivity" data-testid="tab-productivity">
            <Clock className="w-4 h-4 mr-2" />
            Productivity
          </TabsTrigger>
          <TabsTrigger value="communications" data-testid="tab-communications">
            <Phone className="w-4 h-4 mr-2" />
            Communications
          </TabsTrigger>
          <TabsTrigger value="pipeline" data-testid="tab-pipeline">
            <TrendingUp className="w-4 h-4 mr-2" />
            Pipeline
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Cases"
              value={caseReport?.total_cases || 0}
              icon={Briefcase}
              color="text-blue-600"
            />
            <StatCard
              title="Total Revenue"
              value={formatCurrency(billingReport?.totals?.total_billed)}
              subtitle={`${formatCurrency(billingReport?.totals?.outstanding)} outstanding`}
              icon={DollarSign}
              color="text-emerald-600"
            />
            <StatCard
              title="Hours Logged"
              value={formatHours(productivityReport?.summary?.total_hours)}
              subtitle={`${productivityReport?.summary?.utilization_rate || 0}% utilization`}
              icon={Clock}
              color="text-purple-600"
            />
            <StatCard
              title="Communications"
              value={communicationsReport?.summary?.total_communications || 0}
              subtitle={`${formatHours(communicationsReport?.summary?.total_duration_hours)} total duration`}
              icon={Phone}
              color="text-amber-600"
            />
          </div>

          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Cases by Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cases by Status</CardTitle>
              </CardHeader>
              <CardContent>
                {caseReport?.by_status && Object.keys(caseReport.by_status).length > 0 ? (
                  <SimpleBarChart
                    data={Object.entries(caseReport.by_status).map(([status, count]) => ({
                      label: status.charAt(0).toUpperCase() + status.slice(1),
                      value: count
                    }))}
                    labelKey="label"
                    valueKey="value"
                    color="bg-blue-500"
                  />
                ) : (
                  <p className="text-muted-foreground text-center py-8">No case data available</p>
                )}
              </CardContent>
            </Card>

            {/* Cases by Type */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Cases by Type</CardTitle>
              </CardHeader>
              <CardContent>
                {caseReport?.by_type && Object.keys(caseReport.by_type).length > 0 ? (
                  <SimpleBarChart
                    data={Object.entries(caseReport.by_type).map(([type, count]) => ({
                      label: type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                      value: count
                    }))}
                    labelKey="label"
                    valueKey="value"
                    color="bg-purple-500"
                  />
                ) : (
                  <p className="text-muted-foreground text-center py-8">No type data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trends */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Case Creation</CardTitle>
            </CardHeader>
            <CardContent>
              {caseReport?.by_month && caseReport.by_month.length > 0 ? (
                <SimpleBarChart
                  data={caseReport.by_month.slice(-12)}
                  labelKey="month"
                  valueKey="count"
                  color="bg-indigo-500"
                />
              ) : (
                <p className="text-muted-foreground text-center py-8">No monthly data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Billing Tab */}
        <TabsContent value="billing" className="space-y-6">
          {/* Billing Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Billed"
              value={formatCurrency(billingReport?.totals?.total_billed)}
              icon={DollarSign}
              color="text-emerald-600"
            />
            <StatCard
              title="Total Collected"
              value={formatCurrency(billingReport?.totals?.total_collected)}
              icon={CheckCircle}
              color="text-blue-600"
            />
            <StatCard
              title="Outstanding"
              value={formatCurrency(billingReport?.totals?.outstanding)}
              icon={AlertCircle}
              color="text-amber-600"
            />
            <StatCard
              title="Utilization Rate"
              value={`${billingReport?.time_entries?.utilization_rate || 0}%`}
              subtitle={`${formatHours(billingReport?.time_entries?.billable_hours)} billable`}
              icon={Activity}
              color="text-purple-600"
            />
          </div>

          {/* Revenue by Month */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Revenue by Month</CardTitle>
            </CardHeader>
            <CardContent>
              {billingReport?.by_month && billingReport.by_month.length > 0 ? (
                <SimpleBarChart
                  data={billingReport.by_month.slice(-12).map(m => ({
                    month: m.month,
                    amount: m.amount
                  }))}
                  labelKey="month"
                  valueKey="amount"
                  color="bg-emerald-500"
                />
              ) : (
                <p className="text-muted-foreground text-center py-8">No revenue data available</p>
              )}
            </CardContent>
          </Card>

          {/* Top Cases by Revenue */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Cases by Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                {billingReport?.by_case && billingReport.by_case.length > 0 ? (
                  <div className="space-y-3">
                    {billingReport.by_case.slice(0, 8).map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <div className="truncate flex-1 mr-4">
                          <p className="font-medium truncate">{c.case_name}</p>
                          <p className="text-xs text-muted-foreground">{formatHours(c.hours)}</p>
                        </div>
                        <p className="font-bold text-emerald-600">{formatCurrency(c.amount)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No case revenue data</p>
                )}
              </CardContent>
            </Card>

            {/* Revenue by Activity */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Revenue by Activity</CardTitle>
              </CardHeader>
              <CardContent>
                {billingReport?.by_activity && billingReport.by_activity.length > 0 ? (
                  <SimpleBarChart
                    data={billingReport.by_activity.slice(0, 8).map(a => ({
                      activity: a.activity_code,
                      amount: a.amount
                    }))}
                    labelKey="activity"
                    valueKey="amount"
                    color="bg-blue-500"
                  />
                ) : (
                  <p className="text-muted-foreground text-center py-8">No activity data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Invoice Aging */}
          {billingReport?.invoice_aging && billingReport.invoice_aging.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Invoice Aging</CardTitle>
                <CardDescription>Outstanding invoices by age</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Invoice</th>
                        <th className="text-left py-2 px-3">Amount</th>
                        <th className="text-left py-2 px-3">Due Date</th>
                        <th className="text-left py-2 px-3">Days Overdue</th>
                        <th className="text-left py-2 px-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingReport.invoice_aging.map((inv, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-2 px-3 font-medium">{inv.invoice_number || inv.invoice_id.slice(0, 8)}</td>
                          <td className="py-2 px-3">{formatCurrency(inv.amount)}</td>
                          <td className="py-2 px-3">{inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '-'}</td>
                          <td className="py-2 px-3">{inv.days_overdue}</td>
                          <td className="py-2 px-3">
                            <Badge variant={
                              inv.aging_bucket === 'current' ? 'default' :
                              inv.aging_bucket === '1-30' ? 'secondary' :
                              inv.aging_bucket === '31-60' ? 'outline' : 'destructive'
                            }>
                              {inv.aging_bucket}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Productivity Tab */}
        <TabsContent value="productivity" className="space-y-6">
          {/* Productivity Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Hours"
              value={formatHours(productivityReport?.summary?.total_hours)}
              icon={Clock}
              color="text-blue-600"
            />
            <StatCard
              title="Billable Hours"
              value={formatHours(productivityReport?.summary?.billable_hours)}
              subtitle={`${productivityReport?.summary?.utilization_rate || 0}% utilization`}
              icon={DollarSign}
              color="text-emerald-600"
            />
            <StatCard
              title="Cases Worked"
              value={productivityReport?.summary?.cases_worked || 0}
              icon={Briefcase}
              color="text-purple-600"
            />
            <StatCard
              title="Avg Hours/Day"
              value={formatHours(productivityReport?.summary?.avg_hours_per_day)}
              subtitle={`${productivityReport?.summary?.days_worked || 0} days worked`}
              icon={Calendar}
              color="text-amber-600"
            />
          </div>

          {/* Hours by Day of Week */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Hours by Day of Week</CardTitle>
            </CardHeader>
            <CardContent>
              {productivityReport?.hours_by_day_of_week && productivityReport.hours_by_day_of_week.length > 0 ? (
                <SimpleBarChart
                  data={productivityReport.hours_by_day_of_week}
                  labelKey="day"
                  valueKey="hours"
                  color="bg-blue-500"
                />
              ) : (
                <p className="text-muted-foreground text-center py-8">No daily data available</p>
              )}
            </CardContent>
          </Card>

          {/* Weekly Trends and Top Cases */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Weekly Hours</CardTitle>
              </CardHeader>
              <CardContent>
                {productivityReport?.by_week && productivityReport.by_week.length > 0 ? (
                  <SimpleBarChart
                    data={productivityReport.by_week}
                    labelKey="week"
                    valueKey="hours"
                    color="bg-indigo-500"
                  />
                ) : (
                  <p className="text-muted-foreground text-center py-8">No weekly data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Cases by Hours</CardTitle>
              </CardHeader>
              <CardContent>
                {productivityReport?.top_cases && productivityReport.top_cases.length > 0 ? (
                  <div className="space-y-3">
                    {productivityReport.top_cases.slice(0, 6).map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <div className="truncate flex-1 mr-4">
                          <p className="font-medium truncate">{c.case_name}</p>
                          <p className="text-xs text-muted-foreground">{c.entries} entries</p>
                        </div>
                        <p className="font-bold text-blue-600">{formatHours(c.hours)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No case data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Activity Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Time by Activity Type</CardTitle>
            </CardHeader>
            <CardContent>
              {productivityReport?.by_activity && productivityReport.by_activity.length > 0 ? (
                <SimpleBarChart
                  data={productivityReport.by_activity.slice(0, 10)}
                  labelKey="activity_code"
                  valueKey="hours"
                  color="bg-purple-500"
                />
              ) : (
                <p className="text-muted-foreground text-center py-8">No activity data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communications Tab */}
        <TabsContent value="communications" className="space-y-6">
          {/* Communication Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Communications"
              value={communicationsReport?.summary?.total_communications || 0}
              icon={Phone}
              color="text-blue-600"
            />
            <StatCard
              title="Total Duration"
              value={formatHours(communicationsReport?.summary?.total_duration_hours)}
              icon={Clock}
              color="text-purple-600"
            />
            <StatCard
              title="With Recording"
              value={communicationsReport?.summary?.with_recording || 0}
              icon={FileText}
              color="text-amber-600"
            />
            <StatCard
              title="Billable"
              value={communicationsReport?.summary?.billable || 0}
              icon={DollarSign}
              color="text-emerald-600"
            />
          </div>

          {/* Communications by Type and Direction */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">By Communication Type</CardTitle>
              </CardHeader>
              <CardContent>
                {communicationsReport?.by_type && communicationsReport.by_type.length > 0 ? (
                  <SimpleBarChart
                    data={communicationsReport.by_type.map(t => ({
                      type: t.type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown',
                      count: t.count
                    }))}
                    labelKey="type"
                    valueKey="count"
                    color="bg-blue-500"
                  />
                ) : (
                  <p className="text-muted-foreground text-center py-8">No type data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">By Direction</CardTitle>
              </CardHeader>
              <CardContent>
                {communicationsReport?.by_direction ? (
                  <div className="flex gap-6 justify-center py-6">
                    <div className="text-center">
                      <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-2">
                        <span className="text-2xl font-bold text-blue-600">
                          {communicationsReport.by_direction.inbound || 0}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">Inbound</p>
                    </div>
                    <div className="text-center">
                      <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                        <span className="text-2xl font-bold text-emerald-600">
                          {communicationsReport.by_direction.outbound || 0}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">Outbound</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No direction data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Top Contacts and Cases */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Contacts</CardTitle>
              </CardHeader>
              <CardContent>
                {communicationsReport?.top_contacts && communicationsReport.top_contacts.length > 0 ? (
                  <div className="space-y-3">
                    {communicationsReport.top_contacts.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <div>
                          <p className="font-medium">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.minutes} minutes</p>
                        </div>
                        <Badge>{c.count} calls</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No contact data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Cases by Communications</CardTitle>
              </CardHeader>
              <CardContent>
                {communicationsReport?.top_cases && communicationsReport.top_cases.length > 0 ? (
                  <div className="space-y-3">
                    {communicationsReport.top_cases.map((c, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                        <div className="truncate flex-1 mr-4">
                          <p className="font-medium truncate">{c.case_name}</p>
                          <p className="text-xs text-muted-foreground">{c.minutes} minutes</p>
                        </div>
                        <Badge>{c.count}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No case data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monthly Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Communication Volume</CardTitle>
            </CardHeader>
            <CardContent>
              {communicationsReport?.by_month && communicationsReport.by_month.length > 0 ? (
                <SimpleBarChart
                  data={communicationsReport.by_month.slice(-12)}
                  labelKey="month"
                  valueKey="count"
                  color="bg-indigo-500"
                />
              ) : (
                <p className="text-muted-foreground text-center py-8">No monthly data available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pipeline Tab */}
        <TabsContent value="pipeline" className="space-y-6">
          {/* Pipeline Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Total Cases"
              value={pipelineReport?.summary?.total_cases || 0}
              icon={Briefcase}
              color="text-blue-600"
            />
            <StatCard
              title="Conversion Rate"
              value={`${pipelineReport?.conversion_rate || 0}%`}
              subtitle="Progressed past intake"
              icon={TrendingUp}
              color="text-emerald-600"
            />
            <StatCard
              title="Win Rate"
              value={`${pipelineReport?.win_rate || 0}%`}
              subtitle={`${pipelineReport?.summary?.won_cases || 0} won cases`}
              icon={CheckCircle}
              color="text-purple-600"
            />
            <StatCard
              title="Lost Cases"
              value={pipelineReport?.summary?.lost_cases || 0}
              icon={XCircle}
              color="text-red-600"
            />
          </div>

          {/* Case Pipeline Funnel */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Case Pipeline Funnel</CardTitle>
              <CardDescription>Cases by current stage</CardDescription>
            </CardHeader>
            <CardContent>
              {pipelineReport?.funnel && pipelineReport.funnel.length > 0 ? (
                <PipelineFunnel data={pipelineReport.funnel} />
              ) : (
                <p className="text-muted-foreground text-center py-8">No pipeline data available</p>
              )}
            </CardContent>
          </Card>

          {/* Outcomes and Value */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Case Outcomes</CardTitle>
                <CardDescription>Distribution of closed case outcomes</CardDescription>
              </CardHeader>
              <CardContent>
                {pipelineReport?.by_outcome && pipelineReport.by_outcome.length > 0 ? (
                  <SimpleBarChart
                    data={pipelineReport.by_outcome.map(o => ({
                      outcome: o.outcome?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown',
                      count: o.count
                    }))}
                    labelKey="outcome"
                    valueKey="count"
                    color="bg-amber-500"
                  />
                ) : (
                  <p className="text-muted-foreground text-center py-8">No outcome data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Value by Status</CardTitle>
                <CardDescription>Total billed amount by case status</CardDescription>
              </CardHeader>
              <CardContent>
                {pipelineReport?.value_by_status && pipelineReport.value_by_status.length > 0 ? (
                  <div className="space-y-3">
                    {pipelineReport.value_by_status.map((v, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-muted/50 rounded">
                        <div>
                          <p className="font-medium capitalize">{v.status}</p>
                          <p className="text-xs text-muted-foreground">{v.count} cases</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-emerald-600">{formatCurrency(v.total_value)}</p>
                          <p className="text-xs text-muted-foreground">Avg: {formatCurrency(v.avg_value)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No value data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Average Stage Duration */}
          {pipelineReport?.avg_stage_duration && pipelineReport.avg_stage_duration.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Average Time in Each Stage</CardTitle>
                <CardDescription>Based on closed cases</CardDescription>
              </CardHeader>
              <CardContent>
                <SimpleBarChart
                  data={pipelineReport.avg_stage_duration.map(s => ({
                    stage: s.stage?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown',
                    days: s.avg_days
                  }))}
                  labelKey="stage"
                  valueKey="days"
                  color="bg-teal-500"
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
